import { module } from "angular";
import { map } from "rxjs/operators";

import type { Observable } from "rxjs";

import type { IResponse, IRole } from "App/Modules/Models";
import type { IHttpService } from "App/Modules/Services";

import { filter } from "lodash";
import { RolesGroup, RolesGroupFunctions } from "../../Models/Models";
import type { IRolesRepository } from "./IRolesRepository";

export default class RolesRepository implements IRolesRepository {
    public static $inject: string[] = ["mmHttpService", "$translate"];

    constructor(
        private readonly httpService: IHttpService,
        private readonly $translate: angular.translate.ITranslateService,
    ) {}

    public getRoles(): Observable<IResponse<IRole[]>> {
        return this.httpService.get<IRole[]>("roles");
    }

    public getGlobalRoles(): Observable<IResponse<IRole[]>> {
        return this.getRoles().pipe(
            map((response: IResponse<IRole[]>) => this.filterRoles(response, RolesGroup.Global)),
        );
    }

    public getPartnerRoles(): Observable<IResponse<IRole[]>> {
        return this.getRoles().pipe(
            map((response: IResponse<IRole[]>) => this.filterRoles(response, RolesGroup.Partner)),
        );
    }

    public getSiteRoles(): Observable<IResponse<IRole[]>> {
        return this.getRoles().pipe(map((response: IResponse<IRole[]>) => this.filterRoles(response, RolesGroup.Site)));
    }

    public getRolesForSysAdmin(): Observable<IResponse<IRole[]>> {
        return this.getRoles().pipe(
            map((response: IResponse<IRole[]>) => {
                if (response.data) {
                    response.data = filter(response.data, (role) => role.name === "PartnerAdmin").map((role) =>
                        this.addTranslatedLabel(role),
                    );
                }
                return response;
            }),
        );
    }

    public addTranslatedLabel(role: IRole): IRole {
        role.label = this.$translate.instant(`Entities.Role.${role.name}`);
        return role;
    }

    public getNoRole(): IRole {
        return this.addTranslatedLabel({
            id: null,
            name: "NoRole",
        } as IRole);
    }

    private filterRoles(response: IResponse<IRole[]>, roleGroup: RolesGroup): IResponse<IRole[]> {
        if (response.data) {
            response.data = response.data
                .filter((role) => RolesGroupFunctions.RolesGroup(role) === roleGroup)
                .map((role) => this.addTranslatedLabel(role));
        }
        return response;
    }
}

module("mmRepositories").service("irRolesRepository", RolesRepository);
